import { type FC, type ReactNode, useCallback, useRef, useState } from "react"

import { AnimatePresence, motion, useMotionValue, useTransform } from "framer-motion"
import { createPortal } from "react-dom"

import { NeuralNetworkLoader } from "@/shared/ui/loading"

import { useEscapeKey } from "@/shared/lib/hooks"
import { useBodyScrollLock } from "@/shared/lib/interaction-lock"
import { useReducedMotion } from "@/shared/lib/reduced-motion"

import { ModalScrollParentProvider } from "./modal-scroll-parent-context"
import ModalContent from "./ModalContent"
import ModalHeader from "./ModalHeader"
import useDragBehavior from "./useDragBehavior"

export interface TabModalProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** Function to call when the modal is closed */
  onClose: () => void
  /** The title of the modal */
  title: string
  caption?: string
  /** Icon to display next to the title */
  icon?: ReactNode
  /** Rendered right after the title text, e.g. a count badge */
  titleAdornment?: ReactNode
  /** Extra classes merged onto the title heading, e.g. a heavier weight */
  titleClassName?: string
  /** Rendered under the title, e.g. a control that expands part of the body */
  subtitle?: ReactNode
  /** Additional content to render in the header (right side) */
  headerActions?: ReactNode
  /** Children content to render in the modal body */
  children: ReactNode
  /** Custom className for the body content container */
  bodyClassName?: string
  /** Children to render in the tab navigation area (below the header) */
  tabNavigation?: ReactNode
  /** Whether to show a loading indicator in the title */
  isLoading?: boolean
  /** Height of the modal (default is 90vh) */
  height?: string
  /** Function to trigger before closing, can be used to confirm changes */
  onBeforeClose?: () => boolean
  /** Whether to disable haptic feedback */
  disableHaptics?: boolean
  /** Whether to disable drag-to-close functionality */
  disableDrag?: boolean
  /** Whether to prevent all user-driven closing interactions */
  isBlocking?: boolean
  /** Control visibility of the header close button */
  showCloseButton?: boolean
  /** Whether to preserve scroll position of the background content */
  preserveScrollPosition?: boolean
  /** Maximum width for the modal container */
  maxWidth?: string
}

/**
 * Bottom sheet modal with tab navigation and iOS-like dragging behavior
 */
export const TabModal: FC<TabModalProps> = ({
  isOpen,
  onClose,
  title,
  caption,
  icon,
  titleAdornment,
  titleClassName,
  subtitle,
  headerActions,
  children,
  bodyClassName = "",
  tabNavigation,
  isLoading = false,
  height = "98vh",
  onBeforeClose,
  disableHaptics = false,
  disableDrag = false,
  isBlocking = false,
  showCloseButton,
  preserveScrollPosition = true,
  maxWidth = "1000px"
}) => {
  // Refs for the modal elements
  const modalRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLElement>(null)
  const [scrollParent, setScrollParent] = useState<HTMLDivElement | null>(null)
  const handleContentRef = useCallback((node: HTMLDivElement | null) => {
    contentRef.current = node
    setScrollParent(node)
  }, [])

  // Motion value for tracking position
  const y = useMotionValue(0)

  // Use our custom hook to handle all drag behavior
  const [
    { isDragging, isPastThreshold, dragProgress, isContentInteraction, isScrollingContent },
    {
      onDragStart,
      onDrag,
      onDragEnd,
      handleContentPointerDown,
      handleContentTouchStart,
      handleContentTouchMove,
      handleContentTouchEnd,
      setIsContentInteraction
    }
  ] = useDragBehavior({
    y,
    isOpen,
    onClose,
    onBeforeClose,
    modalRef,
    contentRef,
    disableHaptics
  })

  // Transform the dragProgress for visual feedback
  const opacityIndicator = useTransform(useMotionValue(dragProgress), [0, 0.8], [1, 0.6])

  const scaleIndicator = useTransform(useMotionValue(dragProgress), [0, 1], [1, 0.98])

  const reducedMotion = useReducedMotion()
  const resolvedShowCloseButton = showCloseButton ?? !isBlocking
  const shouldDisableDrag = disableDrag || isBlocking || reducedMotion

  // Handle escape key presses
  useEscapeKey({
    isEnabled: isOpen && !isBlocking,
    onEscape: () => {
      const openModals = document.querySelectorAll("[data-tab-modal]")
      if (openModals.item(openModals.length - 1) !== modalRef.current) return
      if (onBeforeClose && !onBeforeClose()) {
        return
      }
      if (isBlocking) {
        return
      }
      onClose()
    }
  })

  // Body scrolling is locked through a shared reference-counted owner so stacked sheets
  // cannot unlock the page for each other, and an abrupt unmount always releases the lock.
  useBodyScrollLock(isOpen, { preserveScrollPosition })

  return (
    <>
      <AnimatePresence>
        {isOpen &&
          createPortal(
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/20"
              style={{
                zIndex: 998,
                pointerEvents: "auto"
              }}
              onClick={() => {
                if (isBlocking) {
                  return
                }
                if (onBeforeClose && !onBeforeClose()) {
                  return
                }
                onClose()
              }}
            />,
            document.body
          )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={modalRef}
            data-tab-modal
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{
              y: isDragging ? y.get() : 0,
              opacity: isDragging ? opacityIndicator.get() : 1,
              scale: isDragging ? scaleIndicator.get() : 1
            }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{
              type: "tween",
              duration: 0.25,
              ease: "easeOut",
              y: isDragging
                ? { duration: 0 }
                : {
                    duration: 0.2,
                    ease: "easeOut"
                  }
            }}
            style={{
              boxShadow: "0 -6px 12px rgba(0, 0, 0, 0.3)",
              y,
              zIndex: 999, // Much higher z-index to ensure it's above all other UI elements
              maxWidth
            }}
            drag={!shouldDisableDrag && !isContentInteraction && !isScrollingContent ? "y" : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.1} // More iOS-like elasticity
            dragTransition={{
              bounceStiffness: 500, // Higher stiffness for snappier feel
              bounceDamping: 40, // Higher damping for less oscillation
              power: 0.2, // Lower power for more precise control
              timeConstant: 80, // Faster response
              modifyTarget: target => {
                // Progressive resistance like iOS
                const modalHeight = modalRef.current?.offsetHeight || 0
                const progress = Math.min(Math.abs(target) / modalHeight, 1)
                const resistance = 1 - Math.pow(progress, 1.5) * 0.6
                return target * resistance
              }
            }}
            dragMomentum={false}
            onDragStart={onDragStart}
            onDrag={onDrag}
            onDragEnd={onDragEnd}
            // Touch listeners to detect whether it's a header or content interaction
            onPointerDown={e => {
              // If this is content interaction, don't allow drag to start
              if (isContentInteraction) return false

              // Check if we're clicking on the header
              const isHeader = (e.target as HTMLElement).closest("header")

              // Only allow drag if we're on header and drag is not disabled
              return !shouldDisableDrag && !!isHeader
            }}
            className="fixed inset-x-0 bottom-0 z-[999] mx-auto w-full overflow-hidden bg-gray-900">
            <div
              className={`flex w-full flex-col overflow-hidden px-2 ${isPastThreshold ? "bg-gray-800" : "bg-gray-900"} transition-colors duration-150`}
              style={{ height }}>
              {/* Header with drag handle */}
              <ModalHeader
                ref={headerRef}
                title={title}
                caption={caption}
                titleAdornment={titleAdornment}
                titleClassName={titleClassName}
                subtitle={subtitle}
                icon={icon}
                headerActions={headerActions}
                isLoading={isLoading}
                isPastThreshold={isPastThreshold}
                dragProgress={dragProgress}
                onClose={onClose}
                tabNavigation={tabNavigation}
                showCloseButton={resolvedShowCloseButton}
                showDragHandle={!shouldDisableDrag}
                onPointerDown={() => {
                  setIsContentInteraction(false)
                }}
                onPointerEnter={() => {
                  setIsContentInteraction(false)
                }}
              />

              {/* Content area with custom scrolling */}
              <ModalScrollParentProvider value={scrollParent}>
                <ModalContent
                  ref={handleContentRef}
                  className={`relative ${bodyClassName}`}
                  onPointerDown={handleContentPointerDown}
                  onPointerEnter={() => {
                    setIsContentInteraction(true)
                  }}
                  onTouchStart={handleContentTouchStart}
                  onTouchMove={handleContentTouchMove}
                  onTouchEnd={handleContentTouchEnd}>
                  <div className="relative flex min-h-[200px] flex-1 flex-col">
                    {children}
                    {isLoading && (
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-950/85">
                        <NeuralNetworkLoader />
                      </div>
                    )}
                  </div>
                </ModalContent>
              </ModalScrollParentProvider>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default TabModal
